import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { includes, findParentNodeOfType, isEqual, isObject, isString, extension, command, keyBinding, NodeExtension, ExtensionTag, isElementDomNode, omitExtraAttributes, nodeInputRule, getMatchString, toggleWrap, isTextSelection, findNodeAtSelection } from '@remirror/core';
import { Slice, Fragment } from '@remirror/pm/model';
import { TextSelection } from '@remirror/pm/state';
import { ExtensionCalloutTheme } from '@remirror/theme';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { ExtensionCalloutMessages } from '@remirror/messages';

var dataAttributeType = 'data-callout-type';
var dataAttributeEmoji = 'data-callout-emoji';
/**
 * Check that the attributes exist and are valid for the codeBlock
 * updateAttributes.
 */

function isValidCalloutAttributes(attributes) {
  if (attributes && isObject(attributes)) {
    var attributesChecklist = Object.entries(attributes).map(_ref => {
      var _ref2 = _slicedToArray(_ref, 2),
          key = _ref2[0],
          value = _ref2[1];

      switch (key) {
        case 'type':
          return !!(isString(value) && value.length > 0);

        case 'emoji':
          return !!(isString(value) && value.length > 0);

        default:
          return true;
      }
    });
    return attributesChecklist.every(attr => !!attr);
  }

  return false;
}
/**
 * Updates the node attrs.
 *
 * This is used to update the type of the callout.
 */

function updateNodeAttributes(type) {
  return (attributes, pos) => _ref3 => {
    var _ref3$state = _ref3.state,
        tr = _ref3$state.tr,
        selection = _ref3$state.selection,
        doc = _ref3$state.doc,
        dispatch = _ref3.dispatch;

    if (!isValidCalloutAttributes(attributes)) {
      throw new Error('Invalid attrs passed to the updateAttributes method');
    }

    var parent = findParentNodeOfType({
      types: type,
      selection: pos ? doc.resolve(pos) : selection
    });

    if (!parent || isEqual(attributes, parent.node.attrs)) {
      // Do nothing since the attrs are the same
      return false;
    }

    tr.setNodeMarkup(parent.pos, type, _objectSpread(_objectSpread({}, parent.node.attrs), attributes));

    if (dispatch) {
      dispatch(tr);
    }

    return true;
  };
}
var DESCRIPTION = ExtensionCalloutMessages.DESCRIPTION,
    LABEL = ExtensionCalloutMessages.LABEL;
var toggleCalloutOptions = {
  icon: _ref4 => {
    var attrs = _ref4.attrs;

    switch (attrs === null || attrs === void 0 ? void 0 : attrs.type) {
      case 'error':
        return 'closeCircleLine';

      case 'success':
        return 'checkboxCircleLine';

      case 'warning':
        return 'errorWarningLine';

      default:
        return 'informationLine';
    }
  },
  description: _ref5 => {
    var t = _ref5.t,
        attrs = _ref5.attrs;
    return t(DESCRIPTION, {
      type: attrs === null || attrs === void 0 ? void 0 : attrs.type
    });
  },
  label: _ref6 => {
    var t = _ref6.t,
        attrs = _ref6.attrs;
    return t(LABEL, {
      type: attrs === null || attrs === void 0 ? void 0 : attrs.type
    });
  }
};
/**
 * Get the callout type from the provided string.
 */

function getCalloutType(value, validTypes, defaultType) {
  return includes(validTypes, value) ? value : defaultType;
}
/**
 * The default emoji render function.
 */

function defaultEmojiRender(node) {
  var emoji = document.createElement('span');
  emoji.textContent = node.attrs.emoji;
  return emoji;
}

var _excluded = ["type", "emoji"];

var _dec, _dec2, _dec3, _dec4, _dec5, _class, _class2;
/**
 * Adds a callout to the editor.
 */

var CalloutExtension = (_dec = extension({
  defaultOptions: {
    defaultType: 'info',
    validTypes: ['info', 'warning', 'error', 'success', 'blank'],
    defaultEmoji: '',
    renderEmoji: defaultEmojiRender
  },
  staticKeys: ['defaultType', 'validTypes', 'defaultEmoji']
}), _dec2 = command(toggleCalloutOptions), _dec3 = command(toggleCalloutOptions), _dec4 = keyBinding({
  shortcut: 'Enter'
}), _dec5 = keyBinding({
  shortcut: 'Backspace'
}), _dec(_class = (_class2 = class CalloutExtension extends NodeExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "tags", [ExtensionTag.Block]);
  }

  get name() {
    return 'callout';
  }

  /**
   * Defines the callout html structure.
   * Adds the returned DOM node form `renderEmoji`  into it.
   */
  createNodeViews() {
    return (node, view, getPos) => {
      var _node$attrs = node.attrs,
          type = _node$attrs.type,
          emoji = _node$attrs.emoji;
      var renderEmoji = this.options.renderEmoji;
      var dom = document.createElement('div');
      var contentDOM = document.createElement('div');
      dom.setAttribute(dataAttributeType, type);

      if (emoji) {
        var emojiWrapper = document.createElement('div');
        var emojiNode = renderEmoji(node, view, getPos);
        dom.setAttribute(dataAttributeEmoji, emoji);
        emojiWrapper.classList.add(ExtensionCalloutTheme.CALLOUT_EMOJI_WRAPPER);

        if (emojiNode) {
          emojiWrapper.append(emojiNode);
          dom.append(emojiWrapper);
        }
      }

      dom.append(contentDOM);
      return {
        dom,
        contentDOM
      };
    };
  }

  createNodeSpec(extra, override) {
    var _override$parseDOM;

    var _this$options = this.options,
        defaultType = _this$options.defaultType,
        validTypes = _this$options.validTypes,
        defaultEmoji = _this$options.defaultEmoji;
    return _objectSpread(_objectSpread({
      content: 'block+',
      defining: true,
      draggable: false
    }, override), {}, {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        type: {
          default: defaultType
        },
        emoji: {
          default: defaultEmoji
        }
      }),
      parseDOM: [{
        tag: "div[".concat(dataAttributeType, "]"),
        getAttrs: node => {
          var _node$getAttribute;

          if (!isElementDomNode(node)) {
            return false;
          }

          var rawType = node.getAttribute(dataAttributeType);
          var emoji = (_node$getAttribute = node.getAttribute(dataAttributeEmoji)) !== null && _node$getAttribute !== void 0 ? _node$getAttribute : '';
          var type = getCalloutType(rawType, validTypes, defaultType);
          var content = node.textContent;
          return _objectSpread(_objectSpread({}, extra.parse(node)), {}, {
            type,
            emoji,
            content
          });
        }
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => {
        var _omitExtraAttributes = omitExtraAttributes(node.attrs, extra),
            type = _omitExtraAttributes.type,
            emoji = _omitExtraAttributes.emoji,
            rest = _objectWithoutProperties(_omitExtraAttributes, _excluded);

        var emojiAttributes = emoji ? {
          [dataAttributeEmoji]: emoji
        } : {};

        var attributes = _objectSpread(_objectSpread(_objectSpread({}, extra.dom(node)), rest), {}, {
          [dataAttributeType]: type
        }, emojiAttributes);

        return ['div', attributes, 0];
      }
    });
  }
  /**
   * Create an input rule that listens for input of 3 colons followed
   * by a valid callout type, to create a callout node
   * If the callout type is invalid, the defaultType callout is created
   */


  createInputRules() {
    return [nodeInputRule({
      regexp: /^:::([\dA-Za-z]*) $/,
      type: this.type,
      beforeDispatch: _ref => {
        var tr = _ref.tr,
            start = _ref.start;
        var $pos = tr.doc.resolve(start);
        tr.setSelection(new TextSelection($pos));
      },
      getAttributes: match => {
        var _this$options2 = this.options,
            defaultType = _this$options2.defaultType,
            validTypes = _this$options2.validTypes;
        return {
          type: getCalloutType(getMatchString(match, 1), validTypes, defaultType)
        };
      }
    })];
  }
  /**
   * Toggle the callout at the current selection. If you don't provide the
   * type it will use the options.defaultType.
   *
   * If none exists one will be created or the existing callout content will be
   * lifted out of the callout node.
   *
   * ```ts
   * if (commands.toggleCallout.enabled()) {
   *   commands.toggleCallout({ type: 'success' });
   * }
   * ```
   */


  toggleCallout() {
    var attributes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return toggleWrap(this.type, attributes);
  }
  /**
   * Update the callout at the current position. Primarily this is used
   * to change the type.
   *
   * ```ts
   * if (commands.updateCallout.enabled()) {
   *   commands.updateCallout({ type: 'error' });
   * }
   * ```
   */


  updateCallout(attributes, pos) {
    return updateNodeAttributes(this.type)(attributes, pos);
  }

  handleEnterKey(_ref2) {
    var dispatch = _ref2.dispatch,
        tr = _ref2.tr;

    if (!(isTextSelection(tr.selection) && tr.selection.empty)) {
      return false;
    }

    var _tr$selection$$from = tr.selection.$from,
        nodeBefore = _tr$selection$$from.nodeBefore,
        parent = _tr$selection$$from.parent;

    if (!nodeBefore || !nodeBefore.isText || !parent.type.isTextblock) {
      return false;
    }

    var regex = /^:::([A-Za-z]*)?$/;
    var text = nodeBefore.text,
        nodeSize = nodeBefore.nodeSize;
    var textContent = parent.textContent;

    if (!text) {
      return false;
    }

    var matchesNodeBefore = text.match(regex);
    var matchesParent = textContent.match(regex);

    if (!matchesNodeBefore || !matchesParent) {
      return false;
    }

    var _this$options3 = this.options,
        defaultType = _this$options3.defaultType,
        validTypes = _this$options3.validTypes;
    var type = getCalloutType(matchesNodeBefore[1], validTypes, defaultType);
    var pos = tr.selection.$from.before();
    var end = pos + nodeSize + 1; // +1 to account for the extra pos a node takes up

    if (dispatch) {
      var slice = new Slice(Fragment.from(this.type.create({
        type
      })), 0, 1);
      tr.replace(pos, end, slice); // Set the selection to within the callout

      tr.setSelection(TextSelection.create(tr.doc, pos + 1));
      dispatch(tr);
    }

    return true;
  }
  /**
   * Handle the backspace key when deleting content.
   */


  handleBackspace(_ref3) {
    var dispatch = _ref3.dispatch,
        tr = _ref3.tr;

    // Aims to stop merging callouts when deleting content in between
    // If the selection is not empty return false and let other extension
    // (ie: BaseKeymapExtension) to do the deleting operation.
    if (!tr.selection.empty) {
      return false;
    }

    var $from = tr.selection.$from; // If not at the start of current node, no joining will happen

    if ($from.parentOffset !== 0) {
      return false;
    }

    var previousPosition = $from.before($from.depth) - 1; // If nothing above to join with

    if (previousPosition < 1) {
      return false;
    }

    var previousPos = tr.doc.resolve(previousPosition); // If resolving previous position fails, bail out

    if (!(previousPos !== null && previousPos !== void 0 && previousPos.parent)) {
      return false;
    }

    var previousNode = previousPos.parent;

    var _findNodeAtSelection = findNodeAtSelection(tr.selection),
        node = _findNodeAtSelection.node,
        pos = _findNodeAtSelection.pos; // If previous node is a callout, cut current node's content into it


    if (node.type !== this.type && previousNode.type === this.type) {
      var content = node.content,
          nodeSize = node.nodeSize;
      tr.delete(pos, pos + nodeSize);
      tr.setSelection(TextSelection.create(tr.doc, previousPosition - 1));
      tr.insert(previousPosition - 1, content);

      if (dispatch) {
        dispatch(tr);
      }

      return true;
    }

    return false;
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "toggleCallout", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleCallout"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "updateCallout", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "updateCallout"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "handleEnterKey", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "handleEnterKey"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "handleBackspace", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "handleBackspace"), _class2.prototype)), _class2)) || _class);

export { CalloutExtension };
